import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import type { AdapterDeps, ServiceAuthAdapter, ServiceKey } from "./auth/types";
import type { StudioModule } from "./studio/types";
import { createDialogRunner } from "./auth/dialog-runner";
import { createSessionTokenStorage, purgeLegacyLocalRefreshTokens } from "./auth/storage";
import {
  buildConnectionRegistry,
  type ConnectionRegistry,
} from "./connection-registry";
import { createServiceFetch, type ServiceFetch } from "./serviceFetch";
import { logConnectorFailure } from "../telemetry";

interface ConnectionRegistryContextValue {
  registry: ConnectionRegistry;
  serviceFetch: ServiceFetch;
}

const ConnectionRegistryContext = createContext<ConnectionRegistryContextValue | null>(null);

interface ProviderProps {
  readonly modules: StudioModule[];
  readonly deps?: Partial<AdapterDeps>;
  readonly children: ReactNode;
}

/**
 * Stable content signature of the connector-relevant fields across all modules.
 * Two `modules` arrays with the same connectors (systemName / name / apiBaseUrl
 * / authConfig) produce the same string, so the registry memo can treat a
 * content-identical background re-check as a no-op instead of rebuilding.
 */
function connectorsSignature(modules: StudioModule[]): string {
  return JSON.stringify(
    modules.map((m) =>
      m.connectors.map((c) => ({
        s: c.systemName,
        n: c.name,
        a: c.apiBaseUrl ?? null,
        c: c.authConfig ?? null,
      })),
    ),
  );
}

export function ConnectionRegistryProvider({ modules, deps, children }: ProviderProps) {
  // The AppConfigProvider's focus/visibility silent re-check (AIVA-7091) re-runs
  // the settings load whenever the pane regains focus and hands down a BRAND-NEW
  // `modules` array on every fire — even when nothing changed. Keying the
  // registry on array identity would rebuild it each time, tearing down live
  // adapters and re-creating them with empty in-memory tokens, which flickers
  // connectors connected↔disconnected every few seconds. Key on a content
  // signature instead so the registry is rebuilt only when the connector set
  // (systemName / name / apiBaseUrl / authConfig) actually changes.
  // AIVA-6685 (V-08): connector refresh tokens now live in sessionStorage. Purge
  // any copies a pre-upgrade session left behind in localStorage once on mount,
  // so the fix removes existing exposure rather than only redirecting new writes.
  useEffect(() => {
    purgeLegacyLocalRefreshTokens();
  }, []);

  const connectorsSig = connectorsSignature(modules);
  const { value, warnings } = useMemo(() => {
    const fullDeps: AdapterDeps = {
      storage: deps?.storage ?? createSessionTokenStorage(),
      dialogRunner: deps?.dialogRunner ?? createDialogRunner(),
      fetchImpl: deps?.fetchImpl,
      now: deps?.now,
    };
    const { registry, warnings } = buildConnectionRegistry(modules, fullDeps);
    return {
      value: { registry, serviceFetch: createServiceFetch(registry, deps?.fetchImpl) },
      warnings,
    };
    // `modules` is read here but intentionally omitted from the dep array —
    // `connectorsSig` captures the content that matters, so a content-identical
    // re-check returns the cached registry instead of churning it.
  }, [connectorsSig, deps]);

  // A connector dropped at build time (no factory, or missing credentials) was
  // console.warn-only, so a misconfigured connector silently vanished from the
  // UI with no telemetry. Report each dropped connector once per build.
  useEffect(() => {
    for (const systemName of warnings.missingAuthConfig) {
      logConnectorFailure(systemName, new Error("connector dropped — missing authConfig"), "missing-auth-config");
    }
    for (const systemName of warnings.unknownAddinKeys) {
      logConnectorFailure(systemName, new Error("connector dropped — no adapter factory"), "unknown-connector-key");
    }
  }, [warnings]);

  useEffect(() => {
    return () => {
      value.registry.dispose();
    };
  }, [value]);

  return (
    <ConnectionRegistryContext.Provider value={value}>
      {children}
    </ConnectionRegistryContext.Provider>
  );
}

export function useConnectionRegistry(): ConnectionRegistry {
  const ctx = useContext(ConnectionRegistryContext);
  if (!ctx) {
    throw new Error("useConnectionRegistry must be used within ConnectionRegistryProvider");
  }
  return ctx.registry;
}

/**
 * Returns the registry if a provider is mounted, or null otherwise — used by
 * standalone Settings tabs so they can render in isolation (storybook,
 * unit tests) without forcing every caller to set up the provider chain.
 */
export function useOptionalConnectionRegistry(): ConnectionRegistry | null {
  const ctx = useContext(ConnectionRegistryContext);
  return ctx?.registry ?? null;
}

export function useConnectionAdapter(key: ServiceKey): ServiceAuthAdapter | undefined {
  // Returns undefined when no provider is mounted — matches the "adapter not
  // registered" semantics, so callers (like stub modules) can render the same
  // either way and tests don't need a provider for snapshot-only assertions.
  const ctx = useContext(ConnectionRegistryContext);
  return ctx?.registry.get(key);
}

export function useServiceFetch(): ServiceFetch {
  const ctx = useContext(ConnectionRegistryContext);
  if (!ctx) {
    throw new Error("useServiceFetch must be used within ConnectionRegistryProvider");
  }
  return ctx.serviceFetch;
}
